import {
    type Calendar,
    CalendarAccessLevel,
    createCalendarAsync,
    createEventAsync,
    EntityTypes,
    getCalendarsAsync,
    getDefaultCalendarAsync,
    PermissionStatus,
    SourceType,
    useCalendarPermissions,
} from "expo-calendar";
import { useEffect, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Dialog } from "react-native-paper";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { CentredModal, type CentredModalProps } from "~/components/layout/modal/CentredModal";
import { BodyText, Title } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { Gap, Padding } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { Alignment } from "~/constants/typography";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { empty, filled, when } from "~/util";
import { convertFrequency } from "~/util/qendo/calendar";

export type SyncCalendarProps = {
    onDismiss: () => void;
} & Omit<CentredModalProps, "children">;

export const SyncCalendar = ({ onDismiss, style, ...modalProps }: SyncCalendarProps) => {
    const { colours } = useTheme();
    const { events } = useJournal();
    const [status, requestPermission] = useCalendarPermissions();
    const [calendars, setCalendars] = useState<Calendar[]>([]);
    const [calendar, setCalendar] = useState<string>("new");
    const { loading, setLoading, addToast } = useFeedback();
    const { profile, is_primary } = useProfile();

    const loadCalendars = () => {
        if (status?.granted) {
            getCalendarsAsync(EntityTypes.EVENT).then((cals) => {
                setCalendars(cals);

                const selected =
                    cals.find((c) => c.name === `qendo-${profile.id}`) ??
                    cals.find((c) => c.name?.startsWith("qendo")) ??
                    cals.find((c) => c.title.includes("QENDO"));

                if (selected) {
                    setCalendar(selected.id);
                } else {
                    getDefaultCalendarAsync()
                        .then(({ id }) => {
                            setCalendar(id);
                        })
                        .catch(() => setCalendar("new"));
                }
            });
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(loadCalendars): callback
    // biome-ignore lint/correctness/useExhaustiveDependencies(requestPermission): api
    useEffect(() => {
        if (status?.granted) {
            loadCalendars();
        } else if (status?.canAskAgain) {
            requestPermission().then((response) => {
                if (response.granted) {
                    loadCalendars();
                }
            });
        }
    }, [status]);

    const denied = status?.status === PermissionStatus.DENIED;
    const can_share = status?.granted && filled(events) && !loading;

    const doShare = async () => {
        if (!can_share) {
            return;
        }

        try {
            setLoading(true);

            const id =
                calendar === "new"
                    ? await createCalendarAsync({
                          title: is_primary ? "QENDO events" : `${profile.name}'s QENDO events`,
                          name: "qendo",
                          color: colours.brand,
                          isVisible: true,
                          isSynced: true,
                          entityType: EntityTypes.EVENT,
                          accessLevel: is_primary ? CalendarAccessLevel.OWNER : CalendarAccessLevel.CONTRIBUTOR,
                          ownerAccount: "qendo",
                          source: {
                              isLocalAccount: true,
                              name: "qendo",
                              type: SourceType.LOCAL,
                          },
                      }).finally(loadCalendars)
                    : calendar;

            if (id) {
                const all_events = events
                    .filter((e) => empty(e.primary_id))
                    .map((e) => ({
                        title: e.title,
                        timeZone: e.time_zone ?? undefined,
                        allDay: e.all_day,
                        startDate: e.start_at,
                        endDate: e.all_day ? undefined : (e.end_at ?? undefined),
                        notes: e.notes ?? "",
                        location: e.location,
                        recurrenceRule:
                            when(e.recurrence, (r) => ({
                                ...convertFrequency(r.frequency, r.interval),
                                endDate: r.end_date ?? undefined,
                                occurrence: r.occurrence ?? undefined,
                            })) ?? null,
                    }));

                await Promise.allSettled(all_events.map((e) => createEventAsync(id, e))).then(() => {
                    addToast({ content: "Events added to calendar" });
                    setTimeout(onDismiss, 10);
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const calendar_items = useMemo(
        () => [
            ...calendars.map(({ id, title, color }) => ({
                value: id,
                label: title,
                icon: () => <QendoIcon name="circle-lg-filled" color={color} />,
            })),
            {
                value: "new",
                label: "New local calendar",
                icon: () => <QendoIcon name="circle-lg-filled" color={colours.brand} />,
            },
        ],
        [calendars, colours],
    );

    return (
        <CentredModal {...modalProps} style={[styles.body, style]} onDismiss={onDismiss}>
            <Dialog.Title style={[Alignment.center, Padding.none]}>
                <Title size="xl" align="center">
                    Share QENDO calendar with phone calendar
                </Title>
            </Dialog.Title>
            <Dialog.Content style={[Padding.vertical_none]}>
                <View style={Gap.sm}>
                    <BodyText align="center">
                        All of the events in your QENDO calendar will appear in your phone calendar.
                    </BodyText>
                    {denied && (
                        <Badge
                            variant="warning"
                            icon="warning-outline"
                            title="You must grant permissions for QENDO to access your calendar."
                        />
                    )}
                    <ComboBox
                        value={calendar}
                        onItemSelected={setCalendar}
                        items={calendar_items}
                        label="Select calendar"
                        disabled={denied}
                    />
                    {empty(events) && (
                        <BodyText align="center" colour="warning">
                            You currently have no events in your calendar
                        </BodyText>
                    )}
                </View>
            </Dialog.Content>
            <Dialog.Actions>
                <Button title="Cancel" variant="quiet" size="sm" onPress={onDismiss} />
                {status?.status === PermissionStatus.UNDETERMINED ? (
                    <Button title="Grant permissions" size="sm" onPress={requestPermission} />
                ) : (
                    <Button title="Share" size="sm" disabled={!can_share} onPress={doShare} />
                )}
            </Dialog.Actions>
        </CentredModal>
    );
};

const styles = StyleSheet.create({
    body: {
        alignItems: "center",
        gap: Space.none,
    },
});
